import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { ToastService } from '../services/toast/toast.service';
import { IPageResponse } from '../../vistoria/models/tipo-vistoria.interface';

/**
 * Resolve a mensagem de erro retornada pelo backend, tolerando os formatos
 * não padronizados entre os serviços. Ordem de prioridade:
 *   1. `erro.error.message` — corpo JSON `{ message: '...' }` (caso mais comum)
 *   2. `erro.error`         — corpo retornado como string pura
 *   3. `erro.message`       — mensagem no nível superior (alguns endpoints),
 *      ignorando o texto técnico gerado pelo próprio Angular
 * Retorna `undefined` quando nenhuma mensagem útil é encontrada — nesse caso
 * o toast genérico deve ser exibido.
 */
export function resolveBackendErrorMessage(erro: any): string | undefined {
  if (typeof erro?.error?.message === 'string') {
    return erro.error.message;
  }
  if (typeof erro?.error === 'string' && erro.error.trim()) {
    return erro.error;
  }
  if (
    typeof erro?.message === 'string' &&
    !erro.message.startsWith('Http failure response')
  ) {
    return erro.message;
  }
  return undefined;
}

/**
 * Tratamento padrão de erro HTTP do sistema:
 * - Exibe a mensagem retornada pelo backend (qualquer formato), independente do
 *   status — inclui os poucos 500 que retornam mensagem.
 * - Quando não há mensagem útil (500 padrão, erro de rede), exibe o toast genérico.
 */
export function handleHttpError(toastService: ToastService, erro: any): void {
  const message = resolveBackendErrorMessage(erro);
  if (message) {
    toastService.showError(message);
  } else {
    toastService.showError();
  }
}

/**
 * Para telas cujo BDD define o texto da indisponibilidade: o 4xx segue com a mensagem do
 * backend e o 5xx ou a falha de rede exibem `mensagemIndisponibilidade` — mesmo quando o
 * corpo do 500 traz o genérico "Erro interno no servidor".
 */
export function handleHttpErrorWithFallback(
  toastService: ToastService,
  erro: HttpErrorResponse,
  mensagemIndisponibilidade: string,
): void {
  if (!erro.status || erro.status >= 500) {
    toastService.showError(mensagemIndisponibilidade);
    return;
  }
  handleHttpError(toastService, erro);
}

export function buildQueryParams(filters: any): HttpParams {
  let params = new HttpParams();

  if (!filters) return params;

  Object.entries(filters).forEach(([key, value]) => {
    // Valor múltiplo (ex.: `contratoIds` do PBI 113881) vira um par repetido
    // `chave=a&chave=b`, que é como o Spring liga uma query string a `List<T>`.
    // Sem isto, `value.toString()` mandaria `a,b` em um par só.
    if (Array.isArray(value)) {
      value
        .filter((item) => item !== null && item !== undefined && item !== '')
        .forEach((item) => {
          params = params.append(key, String(item));
        });
      return;
    }

    if (value !== null && value !== undefined && value !== '') {
      params = params.append(key, value.toString());
    }
  });

  return params;
}

/**
 * Normaliza o `Page` cru do Spring (`size`/`number`) para o `IPageResponse`
 * (`pageSize`/`pageNumber`) que o app consome. Algumas rotas do bilhetagem-api
 * devolvem `ResponseEntity<Page<?>>` em vez do `PageDTO` padrão — sem esta
 * normalização, `pageSize`/`pageNumber` chegam `undefined` e a paginação da
 * listagem volta sempre para a página 0.
 */
export function toPageResponse<T>(raw: any): IPageResponse<T> {
  return {
    ...raw,
    pageSize: raw?.pageSize ?? raw?.size,
    pageNumber: raw?.pageNumber ?? raw?.number,
  };
}

export interface PaginationParams {
  size?: number;
  page?: number;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
}
